import { useCallback, useEffect, useState } from 'react';
import * as api from './api/client';
import { HistoryPanel } from './components/HistoryPanel';
import { LoginGate } from './components/LoginGate';
import { PhoneSearch } from './components/PhoneSearch';
import { SignalGrid } from './components/SignalGrid';
import { SourcesPanel } from './components/SourcesPanel';
import { StatusBar } from './components/StatusBar';
import { disablePush, enablePush, isPushEnabled } from './lib/push';
import type { AuthMe, LookupResponse, RequestRow } from './types';
import './App.css';

function readRequestIdFromUrl(): string | null {
  try {
    const id = new URLSearchParams(window.location.search).get('requestId');
    return id && id.trim() ? id.trim() : null;
  } catch {
    return null;
  }
}

function setRequestIdInUrl(id: string | null) {
  try {
    const url = new URL(window.location.href);
    if (id) url.searchParams.set('requestId', id);
    else url.searchParams.delete('requestId');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  } catch {
    /* ignore */
  }
}

export default function App() {
  const [auth, setAuth] = useState<AuthMe | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LookupResponse | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [history, setHistory] = useState<RequestRow[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [stats, setStats] = useState<{
    total_requests: number;
    ok_requests: number;
    last_24h: number;
  } | null>(null);
  const [region, setRegion] = useState('US');
  const [notify, setNotify] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const refreshMeta = useCallback(async () => {
    const [me, reqs, st] = await Promise.all([api.me(), api.listRequests(40), api.stats()]);
    setAuth(me);
    setHistory(reqs.requests);
    setHistoryTotal(reqs.total);
    setStats(st);
    setPushOn(await isPushEnabled());
  }, []);

  const openRequest = useCallback(async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      const detail = await api.getRequest(id);
      if (!detail.has_full_result || !detail.lookup) {
        setSelectedId(id);
        setRequestIdInUrl(id);
        setResult(null);
        setError(
          detail.error_message
            ? `Log ${id.slice(0, 8)} has no stored payload (${detail.error_message})`
            : `Log ${id.slice(0, 8)} has no full payload (older entry before archive storage). Use RE-RUN for a fresh lookup.`,
        );
        return;
      }
      const lookup: LookupResponse = {
        ...detail.lookup,
        requestId: detail.lookup.requestId || id,
        tracked: true,
        archived: true,
      };
      setResult(lookup);
      setSelectedId(id);
      setRequestIdInUrl(id);
      setFlash('OPENED LOG');
      window.setTimeout(() => setFlash(null), 1400);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to open request');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    api
      .me()
      .then(async (m) => {
        setAuth(m);
        await refreshMeta();
        const fromUrl = readRequestIdFromUrl();
        if (fromUrl) await openRequest(fromUrl);
      })
      .catch(() => setAuth(null))
      .finally(() => setAuthChecked(true));
  }, [refreshMeta, openRequest]);

  // Push notification / SW deep-link while app is already open
  useEffect(() => {
    if (!auth) return;

    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; requestId?: string } | null;
      if (data?.type === 'open-request' && data.requestId) {
        void openRequest(data.requestId);
      }
    };

    const onPopState = () => {
      const id = readRequestIdFromUrl();
      if (id) void openRequest(id);
    };

    navigator.serviceWorker?.addEventListener('message', onMessage);
    window.addEventListener('popstate', onPopState);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', onMessage);
      window.removeEventListener('popstate', onPopState);
    };
  }, [auth, openRequest]);

  const onLogin = async (password: string) => {
    await api.login(password);
    await refreshMeta();
    const fromUrl = readRequestIdFromUrl();
    if (fromUrl) await openRequest(fromUrl);
  };

  const onLogout = async () => {
    await api.logout();
    setAuth(null);
    setResult(null);
    setSelectedId(null);
    setHistory([]);
    setRequestIdInUrl(null);
  };

  const runLookup = async (phone: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.lookup(phone, { defaultRegion: region, notify });
      setResult({ ...res, archived: false });
      setSelectedId(res.requestId);
      setRequestIdInUrl(res.requestId);
      await refreshMeta();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Lookup failed');
    } finally {
      setBusy(false);
    }
  };

  const togglePush = async () => {
    try {
      if (pushOn) {
        await disablePush();
        setPushOn(false);
        setFlash('PUSH OFF');
      } else {
        const r = await enablePush();
        if (r === 'granted') {
          setPushOn(true);
          setFlash('PUSH ON');
        } else {
          setFlash(`PUSH ${r.toUpperCase()}`);
        }
      }
    } catch {
      setFlash('PUSH ERR');
    }
    window.setTimeout(() => setFlash(null), 1600);
  };

  if (!authChecked) {
    return (
      <div className="boot">
        <span className="logo">
          <span className="logo__mark">◈</span> INTEL
        </span>
        <p>BOOTSTRAPPING…</p>
      </div>
    );
  }

  if (!auth) {
    return <LoginGate onLogin={onLogin} />;
  }

  return (
    <div className="app">
      <div className="scanlines" aria-hidden />
      <div className="vignette" aria-hidden />

      <header className="topbar">
        <div className="topbar__brand">
          <span className="logo">
            <span className="logo__mark">◈</span>
          </span>
          <div>
            <h1 className="topbar__title">INTEL</h1>
            <p className="topbar__tag">PHONE NUMBER INTELLIGENCE · TACTICAL HUD</p>
          </div>
          <span className="topbar__ver">v0.1</span>
        </div>
        <div className="topbar__tools">
          <div className="topbar__search">
            <PhoneSearch
              busy={busy}
              defaultRegion={region}
              notify={notify}
              onRegion={setRegion}
              onNotify={setNotify}
              onSubmit={runLookup}
            />
          </div>
          <div className="topbar__actions">
            <button
              type="button"
              className={`btn--toolbar ${pushOn ? 'is-on' : ''}`}
              onClick={togglePush}
              title="Web push notifications"
            >
              PUSH
            </button>
            <button type="button" className="btn--toolbar" onClick={() => refreshMeta()}>
              SYNC
            </button>
            <button type="button" className="btn--toolbar" onClick={onLogout}>
              LOCK
            </button>
          </div>
        </div>
      </header>

      {error && (
        <div className="banner banner--err" role="alert">
          {error}
        </div>
      )}
      {flash && <div className="banner banner--flash">{flash}</div>}

      <main className="grid">
        <div className="panel-signals">
          <SignalGrid result={result} onRerun={runLookup} rerunBusy={busy} />
        </div>
        <div className="panel-sources">
          <SourcesPanel sources={result?.sources ?? []} />
        </div>
        <div className="panel-history">
          <HistoryPanel
            rows={history}
            total={historyTotal}
            selectedId={selectedId}
            onOpen={openRequest}
            onRerun={runLookup}
          />
        </div>
      </main>

      <StatusBar stats={stats} sources={auth.sources} pushOn={pushOn} />
    </div>
  );
}
